import React from 'react';
import PAGINATION_CONFIG from '../../config/paginationConfig';
import './CommonPagination.css';

/**
 * Enterprise LIMS Centralized Pagination Component
 * Unified, deterministic pagination component used across the entire application.
 */
export const CommonPagination = ({
  page = 1,
  pageSize = PAGINATION_CONFIG.defaultPageSize,
  totalRecords = 0,
  onPageChange,
  onPageSizeChange,
  loading = false,
  pageSizeOptions = PAGINATION_CONFIG.pageSizeOptions,
  onExportCurrentPage,
  onExportAll,
  className = '',
  style = {},
}) => {
  const safeTotal = Math.max(0, Number(totalRecords) || 0);
  const safePageSize = Math.max(1, Number(pageSize) || PAGINATION_CONFIG.defaultPageSize);
  const totalPages = Math.max(1, Math.ceil(safeTotal / safePageSize));
  const safePage = Math.min(Math.max(1, Number(page) || 1), totalPages);

  const startRecord = safeTotal === 0 ? 0 : (safePage - 1) * safePageSize + 1;
  const endRecord = Math.min(safePage * safePageSize, safeTotal);

  // Generate windowed page numbers with smart ellipsis
  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = [];
    if (safePage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push('...');
      pages.push(totalPages);
    } else if (safePage >= totalPages - 3) {
      pages.push(1);
      pages.push('...');
      for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push('...');
      pages.push(safePage - 1);
      pages.push(safePage);
      pages.push(safePage + 1);
      pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const handlePageClick = (p) => {
    if (loading || p === safePage || p < 1 || p > totalPages) return;
    onPageChange?.(p);
  };

  const handleSizeChange = (e) => {
    const newSize = Number(e.target.value);
    if (newSize > 0 && onPageSizeChange) {
      onPageSizeChange(newSize);
    }
  };

  return (
    <footer
      className={`lims-pagination-container ${loading ? 'is-loading' : ''} ${className}`}
      style={style}
      aria-label="Table pagination"
    >
      {/* 1. Records Summary */}
      <div className="lims-pagination-info" aria-live="polite">
        {safeTotal === 0
          ? PAGINATION_CONFIG.labels.showingEmpty
          : (
            <>
              Showing <strong>{startRecord}</strong>–<strong>{endRecord}</strong> of <strong>{safeTotal.toLocaleString()}</strong> records
            </>
          )}
      </div>

      {/* 2. Controls & Navigation */}
      <div className="lims-pagination-controls">
        {/* Rows Per Page */}
        {onPageSizeChange && (
          <div className="lims-rows-per-page">
            <span>{PAGINATION_CONFIG.labels.rowsPerPage}:</span>
            <select
              className="lims-rows-select"
              value={safePageSize}
              onChange={handleSizeChange}
              disabled={loading}
              aria-label="Rows per page"
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Buttons: [First] [Previous] 1 2 3 ... 17 [Next] [Last] */}
        <div className="lims-page-nav" role="navigation" aria-label="Pagination Navigation">
          {/* First Button */}
          <button
            type="button"
            className="lims-page-btn"
            disabled={safePage <= 1 || loading}
            onClick={() => handlePageClick(1)}
            title="Go to first page"
            aria-label="First page"
          >
            {PAGINATION_CONFIG.labels.firstShort}
          </button>

          {/* Previous Button */}
          <button
            type="button"
            className="lims-page-btn"
            disabled={safePage <= 1 || loading}
            onClick={() => handlePageClick(safePage - 1)}
            title="Go to previous page"
            aria-label="Previous page"
          >
            {PAGINATION_CONFIG.labels.prevShort}
          </button>

          {/* Numbered Buttons */}
          {getPageNumbers().map((p, idx) => {
            if (p === '...') {
              return (
                <span key={`ellipsis-${idx}`} className="lims-page-ellipsis" aria-hidden="true">
                  …
                </span>
              );
            }
            const isCurrent = p === safePage;
            return (
              <button
                key={`page-${p}`}
                type="button"
                className={`lims-page-btn page-num ${isCurrent ? 'active' : ''}`}
                disabled={loading}
                onClick={() => handlePageClick(p)}
                aria-current={isCurrent ? 'page' : undefined}
                aria-label={`Page ${p}`}
              >
                {p}
              </button>
            );
          })}

          {/* Next Button */}
          <button
            type="button"
            className="lims-page-btn"
            disabled={safePage >= totalPages || loading}
            onClick={() => handlePageClick(safePage + 1)}
            title="Go to next page"
            aria-label="Next page"
          >
            {PAGINATION_CONFIG.labels.nextShort}
          </button>

          {/* Last Button */}
          <button
            type="button"
            className="lims-page-btn"
            disabled={safePage >= totalPages || loading}
            onClick={() => handlePageClick(totalPages)}
            title="Go to last page"
            aria-label="Last page"
          >
            {PAGINATION_CONFIG.labels.lastShort}
          </button>
        </div>

        {/* 3. Export Helper Options (Optional) */}
        {(onExportCurrentPage || onExportAll) && (
          <div className="lims-pagination-export">
            {onExportCurrentPage && (
              <button
                type="button"
                className="lims-export-btn"
                onClick={onExportCurrentPage}
                title="Export current page rows"
              >
                ⬇️ Page ({safeTotal > 0 ? endRecord - startRecord + 1 : 0})
              </button>
            )}
            {onExportAll && (
              <button
                type="button"
                className="lims-export-btn"
                onClick={onExportAll}
                title={`Export all ${safeTotal} filtered rows`}
              >
                📥 All ({safeTotal})
              </button>
            )}
          </div>
        )}
      </div>
    </footer>
  );
};

export default CommonPagination;
